import React, { useCallback, useContext, useMemo, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import BrandedBootLoader from 'components/BrandedBootLoader';
import MaintenanceSplashScreen from 'components/MaintenanceSplashScreen';
import { ALL_CUSTOMERS_MAINTENANCE_MODE_ID } from 'screens/Settings/Advanced/MaintenanceMode/constants';
import isMaintenanceModeActive from 'screens/Settings/Advanced/MaintenanceMode/isMaintenanceModeActive';
import useMaintenanceModeClock from 'screens/Settings/Advanced/MaintenanceMode/useMaintenanceModeClock';

import useMaintenanceModeDoc from './useMaintenanceModeDoc';

const STAFF_BYPASS_SESSION_KEY = 'maintenanceSplashStaffBypass';

export type MaintenanceBannerMessage = {
  key: string;
  message: string;
  // Only staff see these: a reminder that customers are behind a splash screen
  isStaffSplashNotice: boolean;
};

type MaintenanceModeContextType = {
  bannerMessages: MaintenanceBannerMessage[];
  accountSettings: MaintenanceMode.Settings | undefined;
  allCustomersSettings: MaintenanceMode.Settings | undefined;
};

export const MaintenanceModeContext =
  React.createContext<MaintenanceModeContextType>({
    bannerMessages: [],
    accountSettings: undefined,
    allCustomersSettings: undefined,
  });

// Identifies which scope(s) a staff member clicked past, so a splash for a new
// scope (e.g. All Customers on top of this customer) shows again. Edits are
// deliberately not included: staff editing a live splash from inside the app
// shouldn't be kicked back out by their own save.
const getSplashBypassKey = (splashes: MaintenanceMode.Settings[]) =>
  splashes.map((s) => s.id).join('|');

// Sits directly inside CurrentUserProvider: everything above it is lightweight
// identity reads, everything below it (feature gates, datasets, content, GQL,
// routes) is not mounted until we know whether the app is in maintenance.
const MaintenanceModeGate = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { isFleetOpsStaff } = useContext(CurrentUserContext);
  const accountDoc = useMaintenanceModeDoc(selectedAccountId);
  const allCustomersDoc = useMaintenanceModeDoc(
    ALL_CUSTOMERS_MAINTENANCE_MODE_ID,
  );
  const [staffBypassKey, setStaffBypassKey] = useState<string | null>(() =>
    window.sessionStorage.getItem(STAFF_BYPASS_SESSION_KEY),
  );

  const allSettings = useMemo(
    () => [allCustomersDoc.settings, accountDoc.settings],
    [accountDoc.settings, allCustomersDoc.settings],
  );
  const now = useMaintenanceModeClock(allSettings);

  const activeModes = useMemo(
    () =>
      allSettings.filter((settings): settings is MaintenanceMode.Settings =>
        isMaintenanceModeActive({ settings, now }),
      ),
    [allSettings, now],
  );

  const activeSplashes = useMemo(
    () => activeModes.filter((s) => s.mode === 'splash'),
    [activeModes],
  );
  const splashBypassKey = useMemo(
    () => getSplashBypassKey(activeSplashes),
    [activeSplashes],
  );
  const hasStaffBypassedSplash =
    !!isFleetOpsStaff && staffBypassKey === splashBypassKey;

  const onStaffContinue = useCallback(() => {
    window.sessionStorage.setItem(STAFF_BYPASS_SESSION_KEY, splashBypassKey);
    setStaffBypassKey(splashBypassKey);
  }, [splashBypassKey]);

  const bannerMessages = useMemo(
    (): MaintenanceBannerMessage[] =>
      activeModes.map((s) => {
        if (s.mode === 'splash') {
          return {
            key: s.id,
            message: `Maintenance splash screen is active for ${s.accountName}: ${s.message}`,
            isStaffSplashNotice: true,
          };
        }

        return {
          key: s.id,
          message: s.message,
          isStaffSplashNotice: false,
        };
      }),
    [activeModes],
  );

  const value = useMemo(
    () => ({
      bannerMessages,
      accountSettings: accountDoc.settings,
      allCustomersSettings: allCustomersDoc.settings,
    }),
    [accountDoc.settings, allCustomersDoc.settings, bannerMessages],
  );

  if (accountDoc.isLoading || allCustomersDoc.isLoading) {
    return <BrandedBootLoader testId="splash-screen" />;
  }

  if (activeSplashes.length > 0 && !hasStaffBypassedSplash) {
    return (
      <MaintenanceSplashScreen
        splashes={activeSplashes}
        onStaffContinue={isFleetOpsStaff ? onStaffContinue : undefined}
      />
    );
  }

  return (
    <MaintenanceModeContext.Provider value={value}>
      {children}
    </MaintenanceModeContext.Provider>
  );
};

export default MaintenanceModeGate;
